import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, getToken } from "@/lib/api";
import ChoiceQuestion from "./ChoiceQuestion";
import RankQuestion from "./RankQuestion";
import Interlude, { seen } from "./Interlude";
import { AUDIO, JOURNEY, TRANSITIONS } from "@/config/content";

const isValid = (q, v) => {
  if (q.type === "single") return !!v;
  if (q.type === "multi") return Array.isArray(v) && v.length > 0;
  return v && Object.keys(v).length === 4 && [1, 2, 3, 4].every((s) => Object.values(v).includes(s));
};

const draftKey = (qid) => `mapa_draft_${(getToken() || "").slice(0, 16)}_${qid}`;

export default function Questionnaire({ session, data, onSession }) {
  const qmap = useMemo(() => Object.fromEntries(data.questions.map((q) => [q.id, q])), [data]);
  const firstOpen = session.flow.findIndex((id) => !(id in session.answers));
  const [idx, setIdx] = useState(firstOpen === -1 ? session.flow.length - 1 : firstOpen);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const qid = session.flow[idx];
  const q = qmap[qid];
  const [value, setValue] = useState(() => session.answers[qid] ?? JSON.parse(localStorage.getItem(draftKey(qid)) || "null"));

  const goTo = (i, s = session) => {
    const id = s.flow[i];
    setIdx(i);
    setSaved(false);
    setValue(s.answers[id] ?? JSON.parse(localStorage.getItem(draftKey(id)) || "null"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const persist = async (v) => {
    const r = await api.put(`/session/answers/${qid}`, { value: v });
    localStorage.removeItem(draftKey(qid));
    onSession(r.data);
    setSaved(true);
    return r.data;
  };

  const change = (v) => {
    setValue(v);
    setSaved(false);
    localStorage.setItem(draftKey(qid), JSON.stringify(v));
    if (isValid(q, v)) persist(v).catch((e) => toast.error(errMsg(e)));
  };

  const next = async () => {
    if (!isValid(q, value)) return toast.error(q.type === "rank" ? "Atribua as notas 4, 3, 2 e 1, sem repetir." : "Responda para continuar.");
    setBusy(true);
    try {
      const s = saved && qid in session.answers ? session : await persist(value);
      const i = s.flow.indexOf(qid);
      if (i < s.flow.length - 1) goTo(i + 1, s);
      else if (s.flow.length === s.total) {
        const r = await api.post("/session/complete");
        onSession(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const answeredCount = session.flow.filter((id) => id in session.answers).length;
  const pct = Math.round((answeredCount / session.total) * 100);
  const isLast = idx === session.flow.length - 1 && session.flow.length === session.total;
  const [, refresh] = useState(0);
  const pass = (id) => {
    seen.add(id);
    refresh((n) => n + 1);
    window.scrollTo({ top: 0 });
  };

  if (Object.keys(session.answers).length === 0 && !seen.has("welcome")) {
    return <Interlude id="welcome" {...JOURNEY.welcome} audio={AUDIO.welcome} onNext={() => pass("welcome")} />;
  }
  const tr = [].concat(TRANSITIONS[qid] || [])
    .filter((t) => !t.requires || session.flow.includes(t.requires))
    .map((t) => ({ ...t, key: `t_${t.id || qid}` }))
    .find((t) => !seen.has(t.key));
  if (tr && !(qid in session.answers)) {
    const { key, requires, id, ...props } = tr;
    return <Interlude key={key} id={`transition-${id || qid}`} {...props} cta="Continuar" onNext={() => pass(key)} />;
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pb-32 pt-2">
      <div className="sticky top-0 z-20 -mx-5 bg-[#faf7f2]/90 px-5 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between text-xs text-stone-600">
          <span data-testid="progress-label">{idx + 1} de {session.total}</span>
          <span className="flex items-center gap-1" data-testid="autosave-indicator">
            {saved && <><CheckCircle2 size={13} className="text-[#4a6b56]" /> Salvo automaticamente</>}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200" data-testid="progress-bar">
          <div className="h-full rounded-full bg-[#4a6b56] transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div key={qid} className="fade-up pt-6" data-testid={`question-${qid}`}>
        {q.id !== "B1" && <span className="eyebrow">{q.block_title}</span>}
        <h2 className="mt-3 text-2xl leading-snug text-stone-900 sm:text-3xl" data-testid="question-text">
          {q.type === "rank" ? q.text : q.text}
        </h2>
        <div className="mt-7">
          {q.type === "rank"
            ? <RankQuestion q={q} value={value} onChange={change} instruction={data.rank_instruction} />
            : <ChoiceQuestion q={q} value={value} onChange={change} />}
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-[#faf7f2]/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-5 py-3">
          <button type="button" className="btn-ghost" disabled={idx === 0 || busy} onClick={() => goTo(idx - 1)} data-testid="question-back-button">
            <ArrowLeft size={18} /> Voltar
          </button>
          <button type="button" className="btn-primary flex-1 sm:flex-none" disabled={busy || !isValid(q, value)} onClick={next} data-testid="question-next-button">
            {busy ? "Salvando…" : isLast ? "Concluir e gerar meu Mapa" : "Avançar"} {!busy && <ArrowRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
